import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, Clock, Edit3, Flame, GraduationCap, HelpCircle, Sparkles, Star, Trophy, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { HelixLoader } from "@/components/ui/helix-loader";
import { useAuth } from "@/contexts/AuthContext";
import { EditProfileDialog } from "@/components/EditProfileDialog";
import { AtomAvatar } from "@/components/AtomAvatar";
import { parseAvatarState, type AvatarState, fetchUserAvatarConfig } from "@/lib/avatar";
import RecentActivityTimeline from "@/components/profile/RecentActivityTimeline";
import SEO from "@/components/SEO";

type CourseData = {
  id: string;
  slug: string;
  name: string;
  color: string;
  gradeLevel: string;
  lessonsCompleted: number;
  totalLessons: number;
};

function useCountUp(target: number, duration = 1200) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const animate = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(Math.round(eased * target));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return { value, ref };
}

function StatCounter({ target, suffix = "", className = "" }: { target: number; suffix?: string; className?: string }) {
  const { value, ref } = useCountUp(target);
  return (
    <div ref={ref} className={`text-3xl font-bold ${className}`}>
      {value.toLocaleString()}
      {suffix}
    </div>
  );
}

function getInitials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatMemberSince(iso: string | null | undefined) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(date);
  } catch {
    return "";
  }
}

function formatGradeLevelLabel(value: unknown) {
  if (value === null || value === undefined) return "";
  const n = typeof value === "number" ? value : parseInt(String(value).replace(/[^\d]/g, ""), 10);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `Grade ${n}`;
}

function getCourseVisual(title: string): { color: string } {
  const normalizedTitle = title.toLowerCase();
  if (normalizedTitle.includes("astrophysics")) return { color: "#F5A623" };
  if (normalizedTitle.includes("astronomy")) return { color: "#3BBFB8" };
  if (normalizedTitle.includes("quantum") || normalizedTitle.includes("quantom")) return { color: "#7BC843" };
  if (normalizedTitle.includes("physics")) return { color: "#E8527A" };
  return { color: "#7BC843" };
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();
  const userId = session?.user?.id ?? null;

  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const [username, setUsername] = useState("");
  const [memberSince, setMemberSince] = useState("");
  const [totalXP, setTotalXP] = useState(0);
  const [level, setLevel] = useState(0);
  const [streak, setStreak] = useState(0);
  const [courses, setCourses] = useState<CourseData[]>([]);
  const [lessonsCompleted, setLessonsCompleted] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [studyMinutes, setStudyMinutes] = useState(0);
  const [gradeLevelLabel, setGradeLevelLabel] = useState("");
  const [avatarState, setAvatarState] = useState<AvatarState | null>(null);
  const [activities, setActivities] = useState<any[]>([]);

  const [currentProfileForEdit, setCurrentProfileForEdit] = useState<{
    username?: string;
    full_name?: string;
    bio?: string;
    avatar_url?: string;
  }>({});

  const loadProfileData = async (uid: string) => {
    setLoading(true);

    try {
      const { data: profileData } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", uid)
        .maybeSingle();

      const safeUsername = profileData?.username ? String(profileData.username) : "";
      setUsername(safeUsername);
      setMemberSince(formatMemberSince(profileData?.created_at ?? null));
      setLevel(Number(profileData?.level ?? 0) || 0);
      setGradeLevelLabel(formatGradeLevelLabel(profileData?.preferred_grade_level));
      
      const config = await fetchUserAvatarConfig(uid);
      setAvatarState(config);

      setCurrentProfileForEdit({
        username: profileData?.username ?? "",
        full_name: profileData?.full_name ?? "",
        bio: profileData?.bio ?? "",
        avatar_url: profileData?.avatar_url ?? "",
      });

      // Total XP + questions answered
      let xpTotalValue = 0;
      let questionsAnsweredValue = 0;
      try {
        const { data: userStatsData, error: userStatsError } = await supabase
          .from("user_stats")
          .select("xp_total, total_questions")
          .eq("user_id", uid)
          .maybeSingle();

        if (!userStatsError) {
          xpTotalValue = Number(userStatsData?.xp_total ?? 0) || 0;
          questionsAnsweredValue = Number((userStatsData as any)?.total_questions ?? 0) || 0;
        } else {
          const { data: xpOnlyData } = await supabase
            .from("user_stats")
            .select("xp_total")
            .eq("user_id", uid)
            .maybeSingle();
          xpTotalValue = Number(xpOnlyData?.xp_total ?? 0) || 0;
        }
      } catch {
        // ignore
      }

      // Fallback for questions answered if user_stats.total_questions doesn't exist
      if (!questionsAnsweredValue) {
        questionsAnsweredValue = Number((profileData as any)?.total_questions ?? 0) || 0;
      }

      setTotalXP(xpTotalValue);
      setQuestionsAnswered(questionsAnsweredValue);

      // Lessons completed + progress for courses
      const { data: progressData } = await supabase
        .from("user_progress")
        .select("lesson_id, status")
        .eq("user_id", uid);

      const completedLessons = (progressData ?? []).filter((p: any) => p?.status === "completed");
      setLessonsCompleted(completedLessons.length);

      // Streak
      let streakValue = Number((profileData as any)?.streak_count ?? 0) || 0;
      try {
        const { count, error: streakError } = await supabase
          .from("daily_streaks")
          .select("id", { count: "exact", head: true })
          .eq("user_id", uid)
          .eq("completed", true);

        if (!streakError && typeof count === "number") {
          streakValue = count;
        }
      } catch {
        // ignore
      }
      setStreak(streakValue);

      // Study minutes
      let studyMinutesValue = 0;
      try {
        const { data: sessionsData, error: sessionsError } = await supabase
          .from("study_sessions")
          .select("duration_minutes")
          .eq("user_id", uid);

        if (!sessionsError && sessionsData) {
          studyMinutesValue = sessionsData.reduce(
            (acc: number, row: any) => acc + (Number(row?.duration_minutes ?? 0) || 0),
            0,
          );
        }
      } catch {
        // ignore
      }
      setStudyMinutes(studyMinutesValue);

      // Recent activity timeline
      let activityRows: any[] = [];
      try {
        const { data: activityData, error: activityError } = await supabase
          .from("activity_log")
          .select("*")
          .eq("user_id", uid)
          .order("created_at", { ascending: false })
          .limit(20);

        if (!activityError && activityData) {
          activityRows = activityData as any[];
        }
      } catch {
        // ignore
      }
      setActivities(activityRows);

      // Courses started by the user, derived from lesson progress and lesson->course mapping.
      const { data: lessonsData } = await supabase
        .from("lessons")
        .select("id, course_id, is_placeholder")
        .order("order_index", { ascending: true });

      const lessonToCourse = new Map<string, string>();
      const totalLessonsByCourse = new Map<string, number>();

      (lessonsData ?? []).forEach((lesson: any) => {
        if (lesson?.is_placeholder) return;
        if (!lesson?.id || !lesson?.course_id) return;
        const lid = String(lesson.id);
        const cid = String(lesson.course_id);
        lessonToCourse.set(lid, cid);
        totalLessonsByCourse.set(cid, (totalLessonsByCourse.get(cid) ?? 0) + 1);
      });

      const startedCourseIds = new Set<string>();
      const completedLessonsByCourse = new Map<string, number>();

      (progressData ?? []).forEach((p: any) => {
        const lid = p?.lesson_id ? String(p.lesson_id) : null;
        if (!lid) return;
        const cid = lessonToCourse.get(lid);
        if (!cid) return;
        startedCourseIds.add(cid);
        if (p?.status === "completed") {
          completedLessonsByCourse.set(cid, (completedLessonsByCourse.get(cid) ?? 0) + 1);
        }
      });

      const startedCourseIdList = Array.from(startedCourseIds);
      if (startedCourseIdList.length === 0) {
        setCourses([]);
      } else {
        let courseRows: Array<{ id: string; slug: string; title?: string; name?: string }> = [];
        try {
          const { data: courseData, error: courseError } = await supabase
            .from("courses")
            .select("id, slug, title")
            .in("id", startedCourseIdList);
          if (!courseError && courseData) {
            courseRows = courseData as any;
          }
        } catch {
          // ignore
        }

        if (courseRows.length === 0) {
          try {
            const { data: functionCourses } = await supabase.functions.invoke("courses");
            if (Array.isArray(functionCourses)) {
              courseRows = functionCourses.filter((c: any) => startedCourseIds.has(String(c?.id))) as any;
            }
          } catch {
            // ignore
          }
        }

        const gradeLabel = formatGradeLevelLabel(profileData?.preferred_grade_level);
        const mappedCourses: CourseData[] = courseRows
          .map((c) => {
            const name = String((c as any)?.title ?? (c as any)?.name ?? "");
            const slug = String((c as any)?.slug ?? "");
            const cid = String((c as any)?.id ?? "");
            const { color } = getCourseVisual(name);
            return {
              id: cid,
              slug,
              name,
              color,
              gradeLevel: gradeLabel,
              lessonsCompleted: completedLessonsByCourse.get(cid) ?? 0,
              totalLessons: totalLessonsByCourse.get(cid) ?? 0,
            };
          })
          .filter((c) => c.id && c.slug && c.name);

        setCourses(mappedCourses);
      }
    } catch (error) {
      console.error("Error loading profile data:", error);
      setUsername("");
      setMemberSince("");
      setTotalXP(0);
      setLevel(0);
      setStreak(0);
      setCourses([]);
      setLessonsCompleted(0);
      setQuestionsAnswered(0);
      setStudyMinutes(0);
      setGradeLevelLabel("");
      setCurrentProfileForEdit({ username: "", full_name: "", bio: "", avatar_url: "" });
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!userId) {
      navigate("/", { replace: true });
      return;
    }
    loadProfileData(userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, userId]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <HelixLoader className="text-primary mx-auto" />
          <p className="text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO title="My Profile" description="View your progress, streak, and accomplishments on Scico." />
      <style>{`
        .bg-profile-bg { background: hsl(var(--background)); }
        .text-profile-text { color: hsl(var(--foreground)); }

        .glass-card {
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(16px);
        }

        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }

        @keyframes float { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-20px) scale(1.05); } }
        @keyframes float-delayed { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-15px) scale(1.03); } }
        @keyframes float-slow { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-25px) scale(1.07); } }
        @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fade-in { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }

        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-float-delayed { animation: float-delayed 8s ease-in-out infinite 1s; }
        .animate-float-slow { animation: float-slow 10s ease-in-out infinite 2s; }
        .animate-spin-slow { animation: spin-slow 8s linear infinite; }
        .animate-fade-in { animation: fade-in 600ms ease-out both; }
      `}</style>

      <div className="min-h-screen bg-profile-bg font-sans text-profile-text overflow-x-hidden">
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-gradient-to-br from-pink-500/20 to-purple-600/10 blur-3xl animate-float" />
          <div className="absolute top-1/3 -right-24 w-80 h-80 rounded-full bg-gradient-to-br from-cyan-400/15 to-blue-500/10 blur-3xl animate-float-delayed" />
          <div className="absolute bottom-20 left-1/4 w-72 h-72 rounded-full bg-gradient-to-br from-amber-400/15 to-orange-500/10 blur-3xl animate-float-slow" />
          <div className="absolute bottom-1/3 right-1/3 w-64 h-64 rounded-full bg-gradient-to-br from-emerald-400/10 to-teal-500/10 blur-3xl animate-float-delayed" />
        </div>

        <div className="relative z-10">
          <section className="relative px-6 pt-12 pb-8 max-w-3xl mx-auto animate-fade-in">
            <div className="absolute top-12 right-6 flex gap-2">
              <button
                onClick={() => navigate("/avatar")}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 text-white/50 hover:text-white hover:border-white/30 hover:bg-white/5 transition-all text-sm backdrop-blur-sm"
              >
                <Sparkles size={16} className="text-amber-400" />
                Customize Avatar
              </button>
              <button
                onClick={() => setEditDialogOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 text-white/50 hover:text-white hover:border-white/30 hover:bg-white/5 transition-all text-sm backdrop-blur-sm"
              >
                <Edit3 size={16} />
                Edit Profile
              </button>
            </div>

            <div className="flex flex-col items-center gap-4 mt-4">
              <Link 
                to="/avatar"
                role="button"
                aria-label="Customize avatar"
                className="relative group block cursor-pointer"
                onClick={() => console.log('avatar clicked')}
              >
                <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 opacity-70 blur-sm group-hover:opacity-100 transition-opacity animate-spin-slow" />
                <div className="relative w-28 h-28 rounded-full bg-gradient-to-br from-indigo-600 to-purple-700 flex items-center justify-center text-3xl font-bold text-white shadow-xl overflow-hidden">
                  {avatarState ? (
                    <AtomAvatar state={avatarState} size={112} />
                  ) : (
                    getInitials(username || "User")
                  )}
                </div>
                <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
                  <Sparkles size={14} className="text-white" />
                </div>
              </Link>

              <h1 className="text-3xl font-bold bg-gradient-to-r from-white via-purple-200 to-cyan-200 bg-clip-text text-transparent">
                {username || "User"}
              </h1>
              <p className="text-white/40 text-sm flex items-center gap-1.5">
                <Star size={12} className="text-amber-400" fill="currentColor" />
                {memberSince ? `Member since ${memberSince}` : "Member"}
              </p>

              <div className="flex gap-6 mt-6">
                {[
                  {
                    icon: <Zap size={22} />,
                    value: totalXP,
                    label: "Total XP",
                    gradient: "from-amber-400 to-orange-500",
                    glow: "shadow-[0_0_20px_rgba(251,191,36,0.3)]",
                  },
                  {
                    icon: <Trophy size={22} />,
                    value: level,
                    label: "Current Level",
                    gradient: "from-blue-400 to-indigo-500",
                    glow: "shadow-[0_0_20px_rgba(99,102,241,0.3)]",
                  },
                  {
                    icon: <Flame size={22} />,
                    value: streak,
                    label: "Day Streak",
                    gradient: "from-rose-400 to-pink-600",
                    glow: "shadow-[0_0_20px_rgba(244,63,94,0.3)]",
                  },
                ].map((s) => (
                  <div
                    key={s.label}
                    className={`flex flex-col items-center gap-2 glass-card rounded-2xl px-6 py-4 ${s.glow} hover:scale-105 transition-transform`}
                  >
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.gradient} flex items-center justify-center text-white`}>
                      {s.icon}
                    </div>
                    <StatCounter target={s.value} className="text-white" />
                    <span className="text-xs text-white/40">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section
            className="px-6 py-8 max-w-3xl mx-auto animate-fade-in"
            style={{ animationDelay: "0.15s", animationFillMode: "both" }}
          >
            <h2 className="text-xl font-bold mb-5 flex items-center gap-2">
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Your Courses</span>
              <BookOpen size={18} className="text-cyan-400" />
            </h2>

            {courses.length === 0 ? (
              <div className="glass-card rounded-2xl p-10 flex flex-col items-center gap-4 text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <BookOpen size={28} className="text-white" />
                </div>
                <p className="text-white/50">No courses started yet</p>
                <button
                  onClick={() => navigate("/learning")}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold hover:shadow-[0_0_20px_rgba(168,85,247,0.4)] hover:scale-105 transition-all"
                >
                  Start Learning
                </button>
              </div>
            ) : (
              <div className="flex gap-4 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hide">
                {courses.map((course) => {
                  const pct = course.totalLessons > 0 ? (course.lessonsCompleted / course.totalLessons) * 100 : 0;
                  return (
                    <div
                      key={course.id}
                      className="glass-card rounded-2xl p-5 min-w-[220px] flex-shrink-0 flex flex-col gap-3 hover:scale-[1.03] transition-all group"
                      style={{ boxShadow: `0 0 25px ${course.color}30, inset 0 1px 0 ${course.color}15` }}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded-full shadow-lg"
                          style={{ backgroundColor: course.color, boxShadow: `0 0 10px ${course.color}60` }}
                        />
                        <span className="font-semibold text-sm text-white">{course.name}</span>
                      </div>
                      <span className="text-xs text-white/40">{course.gradeLevel}</span>
                      <div className="w-full h-2.5 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${pct}%`,
                            background: `linear-gradient(90deg, ${course.color}, ${course.color}cc)`,
                            boxShadow: `0 0 8px ${course.color}60`,
                          }}
                        />
                      </div>
                      <span className="text-xs text-white/40">
                        {course.lessonsCompleted}/{course.totalLessons} lessons
                      </span>
                      <button
                        onClick={() => navigate(`/learning/${course.slug}`)}
                        className="mt-auto text-sm font-semibold py-2 rounded-xl transition-all hover:shadow-lg"
                        style={{
                          background: `linear-gradient(135deg, ${course.color}20, ${course.color}10)`,
                          border: `1px solid ${course.color}40`,
                          color: course.color,
                        }}
                      >
                        Continue →
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section
            className="px-6 py-8 pb-16 max-w-3xl mx-auto animate-fade-in"
            style={{ animationDelay: "0.3s", animationFillMode: "both" }}
          >
            <h2 className="text-xl font-bold mb-5 bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
              Your Stats
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {[
                {
                  icon: <BookOpen size={22} />,
                  value: lessonsCompleted,
                  label: "Lessons Completed",
                  gradient: "from-emerald-400 to-green-500",
                  glow: "rgba(52,211,153,0.2)",
                },
                {
                  icon: <HelpCircle size={22} />,
                  value: questionsAnswered,
                  label: "Questions Answered",
                  gradient: "from-violet-400 to-purple-500",
                  glow: "rgba(139,92,246,0.2)",
                },
                {
                  icon: <Clock size={22} />,
                  value: studyMinutes,
                  label: "Study Minutes",
                  gradient: "from-cyan-400 to-blue-500",
                  glow: "rgba(34,211,238,0.2)",
                },
                {
                  icon: <GraduationCap size={22} />,
                  label: "Current Grade Level",
                  gradient: "from-amber-400 to-orange-500",
                  glow: "rgba(251,191,36,0.2)",
                  text: gradeLevelLabel || "—",
                },
              ].map((s) => (
                <div
                  key={s.label}
                  className="glass-card rounded-2xl p-5 flex flex-col gap-3 hover:scale-[1.03] transition-all"
                  style={{ boxShadow: `0 0 24px ${s.glow}` }}
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.gradient} flex items-center justify-center text-white`}>
                    {s.icon}
                  </div>
                  {"value" in s && s.value !== undefined ? (
                    <StatCounter target={s.value} className="text-white" />
                  ) : (
                    <div className="text-3xl font-bold text-white">{(s as any).text}</div>
                  )}
                  <span className="text-xs text-white/40">{s.label}</span>
                </div>
              ))}
            </div>
          </section>

          {userId && (
            <section
              className="px-6 py-8 pb-16 max-w-3xl mx-auto space-y-6 animate-fade-in"
              style={{ animationDelay: "0.45s", animationFillMode: "both" }}
            >
              <RecentActivityTimeline activities={activities} />
            </section>
          )}
        </div>

        {userId ? (
          <EditProfileDialog
            open={editDialogOpen}
            onOpenChange={setEditDialogOpen}
            userId={userId}
            currentProfile={currentProfileForEdit}
            onProfileUpdate={() => loadProfileData(userId)}
          />
        ) : null}
      </div>
    </>
  );
}
